import { T, useTranslate } from '@tolgee/react';
import { useFlag } from '@unleash/proxy-client-react';
import { Divider, Dropdown, Menu, Skeleton, Tooltip } from 'antd';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { DeleteSvg, LogoSvg } from '../../../assets/icons';
import ChartSvg from '../../../assets/icons/ChartSvg';
import ChevronRight from '../../../assets/icons/ChevronRight';
import CuripodDevSvg from '../../../assets/icons/CuripodDevLogo';
import ExternalLinkSvg from '../../../assets/icons/ExternalLinkSvg';
import FolderSvg from '../../../assets/icons/FolderSvg';
import HomeSvg from '../../../assets/icons/HomeSvg';
import SettingsOutlinedSvg from '../../../assets/icons/SettingsOutlinedSvg';
import TrainingSvg from '../../../assets/icons/Training';
import UsageGraphSvg from '../../../assets/icons/UsageGraphSvg';
import theme from '../../../assets/theme';
import useAccessBadge, {
  LicenseCoarseLevels,
  LicenseFineLevels,
} from '../../../hooks/useAccessBadge';
import { useMetrics } from '../../../hooks/useMetrics';
import usePaywallPrompt from '../../../hooks/usePaywallPrompt';
import useRouterMatch from '../../../hooks/useRouterMatch';
import useWorkSpace from '../../../hooks/useWorkSpace';
import { usePaywall } from '../../../providers/PaywallProvider/PaywallProvider';
import { isProduction } from '../../../utils/client';
import {
  RESET_HOME_PAGE_STATE_QUERY_PARAM,
  RESET_HOME_PAGE_STATE_QUERY_VALUE,
} from '../../../utils/homePageStateReset';
import { zIndexes } from '../../../utils/zIndexes';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { TeachingCreditCounter } from '../../_atoms/TeachingCreditCounter/TeachingCreditCounter';
import PlanIndicator from '../../_molecules/PlanIndicator';
import CountrySchoolSelectionPopupV2 from '../../CountrySchoolSelectionPopupV2';
import CreateTeam from '../CreateTeam/CreateTeam';

export const TenantsMenuItem = () => {
  const { accessBadge, switchOrganization } = useAccessBadge();
  const {
    teachingCreditState,
    remaining,
    isLessonLimitEnabled,
    showCreditInfo,
  } = usePaywall();

  const [showSchoolSelector, setShowSchoolSelector] = useState(false);

  // Helper function to determine if user has multiple organizations to switch between
  const hasMultipleOrganizations = useMemo(() => {
    return accessBadge?.otherOrganizations && accessBadge.otherOrganizations.length > 0;
  }, [accessBadge?.otherOrganizations]);

  useEffect(() => {
    if (!accessBadge) return;
    setShowSchoolSelector(
      accessBadge.license.coarseLevel !== LicenseCoarseLevels.enterprise &&
        accessBadge.organization?.source === 'User' &&
        (!accessBadge.organization?.reconfirmAt ||
          dayjs().isAfter(dayjs(accessBadge.organization.reconfirmAt))),
    );
  }, [accessBadge]);

  const menu = (
    <Menu key="menu">
      <p
        style={{
          marginLeft: 10,
          color: '#5F6368',
          fontSize: 12,
          userSelect: 'none',
          fontWeight: 'bold',
          marginBottom: 0,
        }}
      >
        <T keyName="Switch school" defaultValue="Switch school" />
      </p>

      {accessBadge &&
        accessBadge.otherOrganizations &&
        accessBadge.otherOrganizations?.map(org => {
          return (
            <Menu.Item key={org.id}>
              <TenantMenuItemContainer
                onClick={() => {
                  switchOrganization(org.id);
                }}
              >
                <IconWrapper width={'18px'} height={'18px'} style={{ marginTop: 2 }}>
                  <ExternalLinkSvg />
                </IconWrapper>
                <div>
                  <p style={{ fontSize: 16, whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                    {org.name}
                  </p>

                  <p
                    style={{
                      width: 'fit-content',
                      fontSize: 12,
                      whiteSpace: 'nowrap',
                      background: '#F3E8D8',
                      color: theme.colors.black,
                      padding: '0px 5px',
                      borderRadius: 5,
                      userSelect: 'none',
                    }}
                  >
                    {org.type === 'Other' ? (
                      <T keyName="School" defaultValue="School" />
                    ) : (
                      org.type
                    )}
                  </p>
                </div>
              </TenantMenuItemContainer>
            </Menu.Item>
          );
        })}
    </Menu>
  );
  return (
    <DropDownContainer>
      {showSchoolSelector && <CountrySchoolSelectionPopupV2 />}
      {accessBadge ? (
        accessBadge.organization ? (
          <Dropdown
            overlayStyle={{
              boxShadow: '0px 2px 20px rgba(0, 0, 0, 0.2)',
              overflowY: 'auto',
              maxHeight: 300,
              zIndex: 2000, // to move the overlay above the appbar
              borderRadius: 10,
            }}
            overlay={menu}
            disabled={!hasMultipleOrganizations}
            trigger={['click']}
          >
            <IconLinkContainer>
              <PlanIndicator isMobileDrawer={false} />
              {hasMultipleOrganizations && (
                <IconWrapperDown>
                  <ChevronRight color={theme.colors.black} />
                </IconWrapperDown>
              )}
            </IconLinkContainer>
          </Dropdown>
        ) : (
          <IconLinkContainer>
            <PlanIndicator isMobileDrawer={false} />
          </IconLinkContainer>
        )
      ) : (
        undefined
      )}
      {!accessBadge && (
        <SkeletonLoader>
          <Skeleton.Button active={true} />
        </SkeletonLoader>
      )}
      {isLessonLimitEnabled && teachingCreditState && remaining !== undefined && (
        <CreditCounterWrapper>
          <TeachingCreditCounter
            remaining={remaining}
            bonusRemaining={teachingCreditState.bonusRemaining}
            weeklyRate={teachingCreditState.weeklyRate}
            used={teachingCreditState.used}
            renewsAt={teachingCreditState.renewsAt}
            onGetMore={() => showCreditInfo('Sidebar')}
          />
        </CreditCounterWrapper>
      )}
    </DropDownContainer>
  );
};

const CreditCounterWrapper = styled.div`
  padding: 0 10px;
`;

const SkeletonLoader = styled.div`
  height: 67px;
  width: 100%;

  .ant-skeleton,
  .ant-skeleton-button {
    height: 100%;

    /* aspect-ratio: 1.28; */
    border-radius: 5px;
    min-width: unset;
    width: 100%;
  }
`;

const NewLiveSideBar = () => {
  const { router, tenantId } = useRouterMatch();

  const { t } = useTranslate();
  const metrics = useMetrics();
  const [showCreateWorkspacePopup, setShowCreateWorkspacePopup] = useState(false);
  const { workspace, isFetching: isFetchingWorkspace } = useWorkSpace();

  const handleCreateTeam = () => {
    metrics.logEvent('CreateTeam', { from: 'Sidebar' });
    setShowCreateWorkspacePopup(true);
  };
  const [showSchoolSelector, setShowSchoolSelector] = useState(false);

  const { userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const isStudent = userAccessBadge?.user.profileInfo?.role === 'student';
  const isSchoolUsageEnabled = useFlag('SCHOOL_USAGE');

  const isParentOrg = useMemo(() => {
    return (userAccessBadge?.otherOrganizations || []).find(
      eachOrg => eachOrg?.parent?.id === userAccessBadge?.organization?.id,
    );
  }, [userAccessBadge]);

  const isDistrictDataLinkVisible =
    userAccessBadge?.organization?.source !== 'User' && isParentOrg;

  const isSchoolUsageLinkVisible =
    isSchoolUsageEnabled &&
    userAccessBadge?.organization?.source !== 'User' &&
    !isParentOrg;

  const [
    requireSchoolSelectionBeforeUsage,
    setRequireSchoolSelectionBeforeUsage,
  ] = useState(true);
  useEffect(() => {
    setRequireSchoolSelectionBeforeUsage(!!!userAccessBadge?.organization?.id);
  }, [userAccessBadge]);

  const isHomePageActive = router.asPath.split('?')[0] === `/${tenantId}/home`;
  return (
    <SideBarContainer>
      <div>
        <CreateTeam
          onDone={() => setShowCreateWorkspacePopup(false)}
          open={showCreateWorkspacePopup}
        />

        <TenantsMenuItem />

        <SideBarItem $isActive={isHomePageActive}>
          <Link
            href={{
              pathname: `/${tenantId}/home`,
              query: {
                [RESET_HOME_PAGE_STATE_QUERY_PARAM]: RESET_HOME_PAGE_STATE_QUERY_VALUE,
              },
            }}
          >
            <a style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <IconWrapper width={'18px'} height={'18px'} style={{ marginRight: '10px' }}>
                <HomeSvg />
              </IconWrapper>{' '}
              <span>
                <T keyName="Home" defaultValue="Home" />
              </span>
            </a>
          </Link>
        </SideBarItem>

        <SideBarItem $isActive={router.asPath === `/${tenantId}/lessons`}>
          <Link href={`/${tenantId}/lessons`}>
            <a style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <IconWrapper width={'18px'} height={'18px'} style={{ marginRight: '10px' }}>
                <FolderSvg />
              </IconWrapper>{' '}
              <span>
                <T keyName="My Lessons" defaultValue="My Lessons" />
              </span>
            </a>
          </Link>
        </SideBarItem>

        <SideBarItem $isActive={router.asPath === `/${tenantId}/reports`}>
          <Link href={`/${tenantId}/reports`}>
            <a style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <IconWrapper width={'18px'} height={'18px'} style={{ marginRight: '10px' }}>
                <ChartSvg />
              </IconWrapper>{' '}
              <span>
                <T keyName="My Reports" defaultValue="My Reports" />
              </span>
            </a>
          </Link>
        </SideBarItem>

        {isSchoolUsageLinkVisible &&
          (requireSchoolSelectionBeforeUsage ? (
            <SideBarItem
              $isActive={router.asPath === `/${tenantId}/usage`}
              onClick={() =>
                metrics.logEvent('SchoolUsage.clicked', { from: 'Homepage Sidebar' })
              }
            >
              <Link href={'/'}>
                <a
                  style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                  onClick={() => setShowSchoolSelector(true)}
                >
                  <IconWrapper
                    width={'18px'}
                    height={'18px'}
                    style={{ marginRight: '10px' }}
                  >
                    <UsageGraphSvg />
                  </IconWrapper>{' '}
                  <span>
                    <T keyName="Your school usage" defaultValue="Your school usage" />
                  </span>
                </a>
              </Link>
            </SideBarItem>
          ) : (
            <SideBarItem
              $isActive={router.asPath === `/${tenantId}/usage`}
              onClick={() =>
                metrics.logEvent('SchoolUsage.clicked', { from: 'Homepage Sidebar' })
              }
            >
              <Link href={`/${tenantId}/usage`}>
                <a style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <IconWrapper
                    width={'18px'}
                    height={'18px'}
                    style={{ marginRight: '10px' }}
                  >
                    <UsageGraphSvg />
                  </IconWrapper>{' '}
                  <span>
                    <T keyName="Your school usage" defaultValue="Your school usage" />
                  </span>
                </a>
              </Link>
            </SideBarItem>
          ))}
        {isDistrictDataLinkVisible && (
          <>
            <SideBarItem $isActive={router.asPath === `/${tenantId}/districtUsage`}>
              <Link href={`/${tenantId}/districtUsage`}>
                <a style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <IconWrapper
                    width={'18px'}
                    height={'18px'}
                    style={{ marginRight: '10px' }}
                  >
                    <UsageGraphSvg />
                  </IconWrapper>{' '}
                  <span>
                    <T keyName="Your District Usage" defaultValue="Your District Usage" />
                  </span>
                </a>
              </Link>
            </SideBarItem>
            <Divider style={{ marginBottom: 0, marginTop: 0 }} />
          </>
        )}
        {!isStudent && workspace && (
          <>
            <Tooltip
              placement="rightTop"
              title={t(
                'All workspace members can access these lessons',
                'All workspace members can access these lessons',
              )}
            >
              <SideBarSubHeader>
                <T keyName="SHARED LIBRARY" defaultValue="SHARED LIBRARY" />
              </SideBarSubHeader>
            </Tooltip>
            <SideBarItem
              $isActive={router.asPath === `/${tenantId}/workspace/${workspace.id}`}
            >
              <Link href={`/${tenantId}/workspace/${workspace.id}`}>
                <a
                  style={{
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {workspace.title}
                </a>
              </Link>
            </SideBarItem>
          </>
        )}
        {!workspace &&
          !isFetchingWorkspace &&
          !isStudent &&
          userAccessBadge?.role === 'Admin' &&
          userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise &&
          userAccessBadge?.license.fineLevel !== LicenseFineLevels.trial && (
            <CreateTeamItem style={{ marginTop: 20 }} onClick={handleCreateTeam}>
              <NewTeamButton type="ghost">
                <T
                  keyName="+ Create School Library"
                  defaultValue="+ Create School Library"
                />
              </NewTeamButton>
            </CreateTeamItem>
          )}

        {showSchoolSelector && <CountrySchoolSelectionPopupV2 />}
      </div>
      <div style={{ flex: 1 }}>{/* Fill remaining height */}</div>
      <Divider style={{ marginBottom: 0, marginTop: 0 }} />
      <SideBarItem $isActive={false}>
        <Link href={`/learn`}>
          <a style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <IconWrapper width={'18px'} height={'18px'} style={{ marginRight: '10px' }}>
              <TrainingSvg />
            </IconWrapper>
            <span>
              <T keyName="Learn More" defaultValue="Learn More" />
            </span>
          </a>
        </Link>
      </SideBarItem>
      <SideBarItem $isActive={router.asPath === `/${tenantId}/profile`}>
        <Link href={`/${tenantId}/profile`}>
          <a style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <IconWrapper width={'18px'} height={'18px'} style={{ marginRight: '10px' }}>
              <SettingsOutlinedSvg />
            </IconWrapper>{' '}
            <span>
              <T keyName="Settings" defaultValue="Settings" />
            </span>
          </a>
        </Link>
      </SideBarItem>
      <SideBarItem $isActive={router.asPath === `/${tenantId}/trash`}>
        <Link href={`/${tenantId}/trash`}>
          <a
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <IconWrapper width={'18px'} height={'18px'} style={{ marginRight: '10px' }}>
              <DeleteSvg />
            </IconWrapper>{' '}
            <span>
              <T keyName="Trash" defaultValue="Trash" />
            </span>
          </a>
        </Link>
      </SideBarItem>
      <ShortLogoContainer>
        {!isProduction() ? <CuripodDevSvg /> : <LogoSvg />}
      </ShortLogoContainer>
    </SideBarContainer>
  );
};

const ShortLogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 20px 20px 30px 20px;
  svg {
    height: 30px;
    width: 80px;
    color: ${theme.colors.orange};
  }
`;

const NewTeamButton = styled(Button)`
  border: none;
  padding: 5px;
  box-shadow: none;
`;

const SideBarContainer = styled.div`
  width: 240px;
  height: 100%;
  padding-left: 10px;
  padding-right: 10px;
  background-color: ${theme.colors.paleBeige};
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
  box-shadow: 0px 4px 4px rgb(0, 0, 0, 0.1);
  z-index: ${zIndexes('SIDEBAR')};
`;

const SideBarItem = styled.div<{ $isActive?: boolean | null }>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  padding: 7px 2px 7px 10px;
  margin: 5px 0px;
  position: static;
  width: 100%;
  left: 0px;
  top: 0px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  border-radius: 5px;
  a {
    color: ${theme.colors.black};
    font-weight: bold;
    font-size: 16px;
    &:hover {
      color: #282f33cc;
    }
  }

  &:hover {
    background-color: #f1eae0;
  }
  background-color: ${({ $isActive }) => ($isActive ? '#F3E8D8' : 'transparent')};
`;

const CreateTeamItem = styled(SideBarItem)`
  &:hover {
    background-color: none !important;
  }
  background-color: transparent !important;
`;

const SideBarSubHeader = styled.p`
  font-weight: 600;
  font-size: 12px;
  color: #666;
  padding-left: 15px;
  margin-top: 20px;
  margin-bottom: 0;
  letter-spacing: 0.08rem;
  user-select: none;
`;

const IconLinkContainer = styled.div<{ $isDisabled?: boolean }>`
  position: relative;
  color: ${theme.colors.black};
  width: 100%;
  height: 100%;
  display: grid;
  border-radius: 5px;
  padding: 10px;
  align-items: center;
  gap: 5px;
  grid-template-columns: 175px 20px; // Had auto but that causes <p> overflows
  cursor: pointer;
  ${({ $isDisabled }) =>
    $isDisabled &&
    `
  opacity: 0.7;
  cursor: progress;
  `};
  & p,
  a {
    color: ${theme.colors.black};
    margin-top: 2px;
    font-weight: bold;
    font-size: 18px;
  }
  svg {
    margin-right: 5px;
  }
  transition: background-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background-color: #f1eae0;
  }
`;

const MenuItemContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${theme.colors.black};
  max-width: 100%;
  p {
    margin-left: 10px;
    margin-bottom: 0;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: calc(100% - 0px);
  }
  span {
    margin-left: 10px;
    margin-bottom: 0;
  }
  svg {
    height: 20px;
    width: 20px;
  }
`;

const TenantMenuItemContainer = styled(MenuItemContainer)`
  display: grid;
  grid-template-columns: 16px auto;
  gap: 4px;
  align-items: start;

  span {
    margin-left: 0px;
  }
  p {
    margin-left: 5px;
  }
  svg {
    margin-left: 0px;
    height: 16px;
    width: 16px;
  }
`;

const DropDownContainer = styled.div`
  width: 100%;
  border-radius: 5px;
  margin: 0px;
  padding: 10px 0px;
`;

const IconWrapperDown = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    transform-origin: center;
    transform: rotate(90deg);
    height: 16px;
    width: 16px;
    margin-right: 0px;
  }
`;

export default NewLiveSideBar;
